import { lazy, Suspense } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { StackHandler } from "@stackframe/react";
import { stackClientApp } from "@/stack/client";
import { NotFoundPage } from "@/pages/not-found";

// ---------------------------------------------------------------------------
// Lazy-loaded page components (code-splitting)
// ---------------------------------------------------------------------------

const DashboardPage = lazy(() =>
  import("@/pages/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const LivePage = lazy(() =>
  import("@/pages/live").then((m) => ({ default: m.LivePage })),
);
const LoginPage = lazy(() =>
  import("@/pages/login").then((m) => ({ default: m.LoginPage })),
);
const SitesPage = lazy(() =>
  import("@/pages/sites").then((m) => ({ default: m.SitesPage })),
);
const SiteDetailPage = lazy(() =>
  import("@/pages/site-detail").then((m) => ({ default: m.SiteDetailPage })),
);
const EventsPage = lazy(() =>
  import("@/pages/events").then((m) => ({ default: m.EventsPage })),
);
const EventDetailPage = lazy(() =>
  import("@/pages/event-detail").then((m) => ({
    default: m.EventDetailPage,
  })),
);
const SearchPage = lazy(() =>
  import("@/pages/search").then((m) => ({ default: m.SearchPage })),
);
const SettingsPage = lazy(() =>
  import("@/pages/settings").then((m) => ({ default: m.SettingsPage })),
);

// ---------------------------------------------------------------------------
// Page loading fallback
// ---------------------------------------------------------------------------

function PageLoader() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stack Auth handler component (processes OAuth callbacks, sign-in/up, etc.)
// ---------------------------------------------------------------------------

function StackHandlerPage() {
  const location = useLocation();
  return <StackHandler app={stackClientApp} location={location.pathname} fullPage />;
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: NotFoundPage,
});

// Stack Auth handler — catch-all for /handler/* paths
const handlerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/handler/$",
  component: StackHandlerPage,
});

// Public: login
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <LoginPage />
    </Suspense>
  ),
});

// Authenticated layout — checks for token before loading child routes
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth",
  beforeLoad: () => {
    const token = localStorage.getItem("cerberus-cloud-token");
    if (!token) {
      throw redirect({ to: "/login" });
    }
  },
  component: () => <Outlet />,
});

// Dashboard (index)
const dashboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <DashboardPage />
    </Suspense>
  ),
});

// Live
const liveRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/live",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <LivePage />
    </Suspense>
  ),
});

// Sites
const sitesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/sites",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SitesPage />
    </Suspense>
  ),
});

const siteDetailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/sites/$siteId",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SiteDetailPage />
    </Suspense>
  ),
});

// Events
const eventsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/events",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <EventsPage />
    </Suspense>
  ),
});

const eventDetailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/events/$eventId",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <EventDetailPage />
    </Suspense>
  ),
});

// Search
const searchRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/search",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SearchPage />
    </Suspense>
  ),
});

// Settings
const settingsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/settings",
  component: () => (
    <Suspense fallback={<PageLoader />}>
      <SettingsPage />
    </Suspense>
  ),
});

// Build route tree
const routeTree = rootRoute.addChildren([
  handlerRoute,
  loginRoute,
  authLayoutRoute.addChildren([
    dashboardRoute,
    liveRoute,
    sitesRoute,
    siteDetailRoute,
    eventsRoute,
    eventDetailRoute,
    searchRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

// Type registration for TanStack Router
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
